<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Feature, PageName } from '@/api/tracking';
import { INITIAL_VISIBLE_DOCUMENT_COUNT } from '@/constants';
import AdpDocumentList from '@/components/common/adp-document/adp-document-list.vue';
import FeedDateSelectbox from '@/components/common/feed-date-selectbox.vue';
import {
  createTechnicalLiteratureShouldEnable,
  useAutoGenerateSummary,
} from '@/components/feed-summary/composables/use-auto-generate-summary';
import { useThemeSummary } from '@/components/feed-summary/composables/use-theme-summary';
import FeedSummaryGenerationButton from '@/components/feed-summary/feed-summary-generation-button.vue';
import FeedSummary from '@/components/feed-summary/feed-summary.vue';
import {
  ArticleMarkSource,
  FeedType,
  Theme,
  ThemeFeedPatent,
  ThemeFeedResearchPaper,
} from '@/types';

type Props = {
  docType: 'research_paper' | 'patent';
  feedType: FeedType;
  formattedFeedDate?: string;
  domesticDocuments: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
  foreignDocuments: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
  theme: Theme;
  enableToEditTheme?: boolean;
  dateOptions: { label: string; value: string }[];
  isPast: boolean;
  feedDate: string;
};
const props = defineProps<Props>();

const emit = defineEmits<{
  'select-date': [value: string | undefined];
}>();

const title = props.docType === 'research_paper' ? '論文ジャーナル' : '特許';

const maxFeedCount = 200;

const pageName: PageName = `${props.docType}s`;
const feature: Feature = `${props.docType}_feed`;
const markSource: ArticleMarkSource = 'theme';

const foreignPaperProps = computed(() => ({
  title: `海外${title}`,
  formattedFeedDate: props.formattedFeedDate,
  displayFeedType: props.feedType,
  themeFeedType: 'foreign' as const,
  docType: props.docType,
  theme: props.theme,
  enableToEditTheme: props.enableToEditTheme,
  initialFeedCount: INITIAL_VISIBLE_DOCUMENT_COUNT,
  maxFeedCount,
  pageName,
  feature,
  markSource,
}));
const foreignPaperDocsProp = computed(() => {
  if (props.theme?.is_foreign_media_enabled) {
    return props.foreignDocuments;
  } else {
    return [];
  }
});

const initialDate = props.dateOptions[props.isPast ? 1 : 0].value;
const currentDate = ref(initialDate);
const selectDate = (value: string | undefined) => {
  if (value === undefined) return;

  emit('select-date', value);

  // セレクトボックスの表示を強制的に戻すために、
  // 選択された値を代入してから元の値を入れ直して変更検知を走らせる
  currentDate.value = value;
  nextTick(() => {
    currentDate.value = initialDate;
  });
};

const articleCount = computed(() => {
  if (props.feedType === 'domestic') {
    return props.domesticDocuments.length;
  } else if (props.feedType === 'foreign') {
    return props.foreignDocuments.length;
  }
  return props.domesticDocuments.length + props.foreignDocuments.length;
});

const { summary, generateButtonState, generateSummary, shouldShowSummary } =
  useThemeSummary(
    {
      page_type: props.isPast ? 'past_feed' : 'feed',
      feed_type: props.feedType,
      model_type: props.docType,
      date: props.feedDate,
      theme_id: props.theme.id,
    },
    articleCount,
    pageName,
  );

// URLにsummarizeパラメータがあったら自動で要約生成する
// isPastでないとき（最新日付 = 先頭）は画面遷移時にも自動生成する
const { autoGenerationReason } = useAutoGenerateSummary(
  generateButtonState,
  generateSummary,
  feature,
  createTechnicalLiteratureShouldEnable({
    isPast: props.isPast,
    dateOptions: props.dateOptions,
  }),
);

// 過去文献で自動生成が有効ならスクロールする
watch(autoGenerationReason, autoGenReason => {
  if (autoGenReason !== undefined && props.isPast) {
    emit('select-date', props.dateOptions[1].value);
  }
});
</script>

<template>
  <div class="technical-literature-list">
    <div class="date-header">
      <FeedDateSelectbox
        :options="dateOptions"
        :model-value="currentDate"
        @update:model-value="selectDate"
      />
      <FeedSummaryGenerationButton
        :state="generateButtonState"
        class="generation-button"
        @click="generateSummary()"
      />
    </div>
    <hr class="header-divider" />
    <FeedSummary
      v-if="shouldShowSummary(summary)"
      :feed-summary="summary"
      :feature="feature"
      :page-name="pageName"
      :theme-id="theme.id"
      class="feed-summary"
    />
    <template v-if="feedType === 'all' || feedType === 'domestic'">
      <AdpDocumentList
        :title="`国内${title}`"
        :formatted-feed-date="formattedFeedDate"
        :display-feed-type="feedType"
        :theme-feed-type="'domestic'"
        :doc-type="docType"
        :theme="theme"
        :enable-to-edit-theme="enableToEditTheme"
        :adp-documents="domesticDocuments"
        :initial-feed-count="INITIAL_VISIBLE_DOCUMENT_COUNT"
        :max-feed-count="maxFeedCount"
        :page-name="pageName"
        :feature="feature"
        :mark-source="markSource"
      />
    </template>
    <template
      v-if="
        docType === 'research_paper' &&
        (feedType === 'all' || feedType === 'foreign')
      "
    >
      <AdpDocumentList
        v-bind="foreignPaperProps"
        :adp-documents="foreignPaperDocsProp"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.technical-literature-list {
  display: flex;
  flex-direction: column;

  background: $color-white;
  border-radius: 4px;
}

.date-header {
  position: sticky;
  top: var(--layout-sticky-top-under-tabs, 112px);
  z-index: 1;

  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 16px;
  background: $color-white;
  border-radius: 4px;

  .generation-button {
    margin-left: auto;
  }
}

.header-divider {
  border: none;
  border-top: 1px solid $color-gray200;
  padding-bottom: 16px;
  background: $color-white;
}

.feed-summary {
  margin: 0 12px 16px;
}
</style>
